import axios from "axios";
import { apiAddress } from "../../config/server";
import {
  ABAGenerationRequest,
  ABAPreviewRequest,
  ABAPreviewResponse,
  IPayEvent,
  IPayEventUpdate,
  IPayEventWithDetails,
  IUnpaidAppointmentsFilters,
  IUnpaidAppointmentsResponse,
  OriginatorBankValidationRequest,
  OriginatorBankValidationResponse,
  PersonBankDetails
} from "../../types/aba";
import { ApiBase } from "./baseApi";

export default class ABAApi extends ApiBase {
  static BSB_URL = apiAddress + "/api/bsb/";
  static BANK_DETAILS_URL = apiAddress + "/api/bank-details/";
  static ADMIN_BANK_DETAILS_URL = apiAddress + "/api/admin/bank-details/";
  static PAY_EVENTS_URL = apiAddress + "/api/pay-events/";

  // ===================
  // ABA Generation & Preview
  // ===================

  /**
   * Generate an ABA file for a pay event
   */
  static async generateABA(
    payEventId: string,
    originatorBank: ABAGenerationRequest["originatorBank"],
    filename: string,
  ): Promise<{ success: boolean; filename?: string; fileHash?: string; blob?: Blob; error?: string; errors?: string[] }> {
    const url = `${this.PAY_EVENTS_URL}${payEventId}/export/aba`;
    const body = { originatorBank };

    try {
      // Make the request with blob response type, bypassing the base class processing
      const response = await axios.post(url, body, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          // Include any auth headers that might be needed
          ...axios.defaults.headers.common
        }
      });

      // Check if response is an error (JSON) or a file (blob) by checking content type
      const contentType = response.headers['content-type'] || '';

      if (contentType.includes('application/json')) {
        // This is an error response sent as JSON
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          error: errorData.error || 'Failed to generate ABA file',
          errors: errorData.errors
        };
      } else if (contentType.includes('application/octet-stream')) {
        // This is a successful file response

        const _filename = response.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1] || filename || 'export.aba';
        const fileHash = response.headers['x-file-hash'];

        return {
          success: true,
          filename: _filename,
          fileHash,
          blob: response.data
        };
      } else {
        return {
          success: false,
          error: 'Unexpected response format'
        };
      }
    } catch (error: any) {
      // Handle network errors or HTTP error status codes
      if (error.response) {
        const contentType = error.response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          // Error response as JSON
          try {
            let errorData;
            if (error.response.data instanceof Blob) {
              const text = await error.response.data.text();
              errorData = JSON.parse(text);
            } else {
              errorData = error.response.data;
            }

            return {
              success: false,
              error: errorData.error || 'Failed to generate ABA file',
              errors: errorData.errors
            };
          } catch (parseError) {
            return {
              success: false,
              error: `Server error: ${error.response.status}`
            };
          }
        } else {
          return {
            success: false,
            error: `Server error: ${error.response.status} ${error.response.statusText}`
          };
        }
      }

      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Preview what will be included in an ABA export
   */
  static async previewABA(
    request: ABAPreviewRequest
  ): Promise<ABAPreviewResponse> {
    const url = ABAApi.PAY_EVENTS_URL + "preview";
    const body = request;

    return ABAApi.post<ABAPreviewResponse>(url, body).then(
      (resp) => resp.data.data as ABAPreviewResponse
    );
  }

  /**
   * Validate originator bank details
   */
  static async validateOriginatorBank(
    request: OriginatorBankValidationRequest
  ): Promise<OriginatorBankValidationResponse> {
    const url = ABAApi.BANK_DETAILS_URL + "validate-originator";
    const body = request;

    return ABAApi.post<OriginatorBankValidationResponse>(url, body).then(
      (resp) => resp.data.data as OriginatorBankValidationResponse
    );
  }

  // ===================
  // Pay Events Management
  // ===================

  /**
   * Get all pay events for an association
   */
  static getPayEvents(associationId: string): Promise<IPayEvent[]> {
    const url = `${this.PAY_EVENTS_URL}association/${associationId}`;
    return ABAApi.get<IPayEvent[]>(url).then((resp) => resp.data.data || []);
  }

  /**
   * Get a specific pay event with details
   */
  static async getPayEvent(
    payEventId: string
  ): Promise<{ data: IPayEventWithDetails; persons?: any[] }> {
    const url = ABAApi.PAY_EVENTS_URL + payEventId;

    return ABAApi.get<IPayEventWithDetails>(url).then(
      (resp) => resp.data
    );
  }

  /**
   * Create a new pay event
   */
  static createPayEvent(payEvent: IPayEventUpdate): Promise<IPayEvent> {
    const url = this.PAY_EVENTS_URL;
    return ABAApi.post<IPayEvent>(url, payEvent).then(
      (resp) => resp.data.data,
    );
  }

  /**
   * Update an existing pay event
   */
  static updatePayEvent(payEvent: IPayEventUpdate): Promise<IPayEvent> {
    const url = `${this.PAY_EVENTS_URL}${payEvent._id}`;
    return ABAApi.patch<IPayEvent>(url, payEvent).then(
      (resp) => resp.data.data,
    );
  }

  /**
   * Delete a pay event
   */
  static deletePayEvent(payEventId: string): Promise<boolean> {
    const url = `${this.PAY_EVENTS_URL}${payEventId}`;
    return ABAApi.delete(url).then(() => true);
  }

  /**
   * Change pay event state (e.g., mark as paid)
   */
  static changePayEventState(
    payEventId: string,
    newState: string,
    reason?: string
  ): Promise<IPayEvent> {
    const url = `${this.PAY_EVENTS_URL}${payEventId}/state`;
    const body = { newState, reason };
    return ABAApi.post<IPayEvent>(url, body).then((resp) => resp.data.data || resp.data);
  }

  // ===================
  // Personal Payment History
  // ===================

  /**
   * Get payment history for a person (shows pay events they were included in)
   */
  static getPersonPaymentHistory(personId?: string): Promise<any[]> {
    const url = `${this.PAY_EVENTS_URL}payment-history/person/${personId || ''}`;
    return ABAApi.get<any[]>(url).then((resp) => resp.data.data || []);
  }

  // ===================
  // Unpaid Appointments
  // ===================

  /**
   * Get unpaid appointments for an association with filtering options
   */
  static async getUnpaidAppointments(
    associationId: string,
    filters?: IUnpaidAppointmentsFilters
  ): Promise<IUnpaidAppointmentsResponse> {
    const url = ABAApi.PAY_EVENTS_URL + "appointments/unpaid/" + associationId;
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const urlWithParams = params.toString() ? `${url}?${params.toString()}` : url;

    return ABAApi.get(urlWithParams).then((resp) => {
      const responseData = resp.data as any;
      return {
        appointments: responseData.data || [],
        persons: responseData.persons || [],
        venues: responseData.venues || [],
        pagination: responseData.pagination || {}
      } as IUnpaidAppointmentsResponse;
    });
  }

  /**
   * Create a pay event from appointments
   */
  static createAppointmentPayEvent(
    params: {
      name: string,
      description: string,
      associationId: string,
      appointmentIds: string[],
      options?: { requireBankDetails?: boolean }
    }
  ): Promise<IPayEvent> {
    const url = this.PAY_EVENTS_URL;
    const body = {
      name: params.name,
      description: params.description,
      associationId: params.associationId,
      type: 'appointments',
      appointmentIds: params.appointmentIds,
      ...(params.options?.requireBankDetails !== undefined && { requireBankDetails: params.options.requireBankDetails })
    };

    return ABAApi.post<IPayEvent>(url, body).then((resp) => resp.data.data || resp.data);
  }

  // ===================
  // Person Bank Details (Recipient side)
  // ===================

  /**
   * Get person's bank details for ABA payments
   * Uses self-service endpoint for own details, admin endpoint for others
   */
  static getPersonBankDetails(personId: string, associationId: string, isSelf: boolean = false): Promise<PersonBankDetails> {
    const url = isSelf
      ? `${this.BANK_DETAILS_URL}?associationId=${associationId}`
      : `${this.ADMIN_BANK_DETAILS_URL}${personId}?associationId=${associationId}`;
    return ABAApi.get<PersonBankDetails>(url).then((resp) => resp.data.data || resp.data);
  }

  /**
   * Create person's bank details for ABA payments
   * Uses self-service endpoint for own details, admin endpoint for others
   */
  static createPersonBankDetails(
    personId: string,
    associationId: string,
    bankDetails: Omit<PersonBankDetails, 'personId' | 'isValid' | 'lastValidated' | 'lastFourDigits' | 'bankName'>,
    isSelf: boolean = false,
  ): Promise<PersonBankDetails> {
    const url = isSelf
      ? `${this.BANK_DETAILS_URL}?associationId=${associationId}`
      : `${this.ADMIN_BANK_DETAILS_URL}${personId}?associationId=${associationId}`;
    const method = isSelf ? 'post' : 'put';

    return ABAApi[method]<PersonBankDetails>(url, bankDetails).then(
      (resp) => resp.data.data || resp.data,
    );
  }

  /**
   * Update person's bank details for ABA payments
   * Uses self-service endpoint for own details, admin endpoint for others
   */
  static updatePersonBankDetails(
    personId: string,
    associationId: string,
    bankDetails: Partial<PersonBankDetails>,
    isSelf: boolean = false,
  ): Promise<PersonBankDetails> {
    const url = isSelf
      ? `${this.BANK_DETAILS_URL}?associationId=${associationId}`
      : `${this.ADMIN_BANK_DETAILS_URL}${personId}?associationId=${associationId}`;
    const method = isSelf ? 'post' : 'put';

    return ABAApi[method]<PersonBankDetails>(url, bankDetails).then(
      (resp) => resp.data.data || resp.data,
    );
  }

  /**
   * Create or update person's bank details (upsert operation)
   */
  static savePersonBankDetails(
    personId: string,
    associationId: string,
    bankDetails: Omit<PersonBankDetails, 'personId' | 'isValid' | 'lastValidated' | 'lastFourDigits' | 'bankName'>,
    isUpdate: boolean = false,
    isSelf: boolean = false,
  ): Promise<PersonBankDetails> {
    if (isUpdate) {
      return this.updatePersonBankDetails(personId, associationId, bankDetails, isSelf);
    } else {
      return this.createPersonBankDetails(personId, associationId, bankDetails, isSelf);
    }
  }

  /**
   * Validate person's bank details
   */
  static validatePersonBankDetails(
    associationId: string,
    bankDetails: { bsb: string; accountNumber: string; accountName: string },
  ): Promise<{ isValid: boolean; errors?: string[] }> {
    const url = `${this.BANK_DETAILS_URL}validate?associationId=${associationId}`;
    return ABAApi.post<{ isValid: boolean; errors?: string[] }>(url, bankDetails).then(
      (resp) => resp.data.data || resp.data,
    );
  }

  /**
   * Look up BSB information
   */
  static async lookupBSB(bsb: string): Promise<{ success: boolean; data?: any; error?: string }> {
    const url = `${this.BSB_URL}${bsb}`;
    try {
      const resp = await ABAApi.get(url);
      // Handle the GenericTransport wrapper - return the actual response data
      return {
        success: resp.data.success ?? true,
        data: resp.data.data || resp.data,
        error: resp.data.error
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'BSB lookup failed'
      };
    }
  }

  /**
   * Validate BSB
   */
  static async validateBSB(bsb: string): Promise<{ success: boolean; isValid?: boolean; data?: any; error?: string }> {
    const url = `${this.BSB_URL}validate/${bsb}`;
    try {
      const resp = await ABAApi.get(url);
      // Handle the GenericTransport wrapper - return the actual response data
      const responseData = resp.data as any;
      return {
        success: responseData.success ?? true,
        isValid: responseData.isValid,
        data: responseData.data || responseData,
        error: responseData.error
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'BSB validation failed'
      };
    }
  }

  /**
   * Search BSBs by bank name
   */
  static async searchBSBs(query: string, limit?: number): Promise<{ success: boolean; data?: any[]; error?: string }> {
    const url = `${this.BSB_URL}search?q=${encodeURIComponent(query)}${limit ? `&limit=${limit}` : ''}`;
    try {
      const resp = await ABAApi.get(url);
      // Handle the GenericTransport wrapper - return the actual response data
      const responseData = resp.data as any;
      return {
        success: responseData.success ?? true,
        data: Array.isArray(responseData.data) ? responseData.data : (responseData.data ? [responseData.data] : []),
        error: responseData.error
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'BSB search failed'
      };
    }
  }

  /**
   * Trigger BSB directory update from server
   */
  static async triggerBSBUpdate(): Promise<{ success: boolean; message?: string; error?: string }> {
    const url = `${this.BSB_URL}update`;
    try {
      const resp = await ABAApi.post(url).then((resp) => {
        const data = resp.data as any;
        return {
          success: true,
          message: data.message || data.data?.message || 'BSB directory updated successfully'
        };
      });
      return resp;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to trigger BSB update'
      };
    }
  }

  /**
   * Upload CSV file to update BSB directory
   */
  static async uploadBSBCSV(file: File): Promise<{
    success: boolean;
    data?: {
      message: string;
      stats: any;
      updateId: string;
      errors?: Array<{ bsb: string; error: string }>;
    };
    error?: string;
  }> {
    const url = `${this.BSB_URL}upload-csv`;
    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;

      return {
        success: data.success ?? true,
        data: data.data,
        error: data.error
      };

    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || `Server error: ${error.response.status}`
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload CSV'
      };
    }
  }

  /**
   * Get BSB directory statistics (admin only)
   */
  static async getBSBStats(): Promise<{ success: boolean; data?: { stats: any }; error?: string }> {
    const url = ABAApi.BSB_URL + "stats";
    try {
      const resp = await ABAApi.get(url);
      return {
        success: true,
        data: (resp.data.data || resp.data) as any
      };
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || `Server error: ${error.response.status}`
        };
      }
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Get recent BSB update logs (admin only)
   */
  static async getBSBUpdateLogs(limit: number = 10): Promise<{ success: boolean; data?: any[]; error?: string }> {
    const url = ABAApi.BSB_URL + "logs?limit=" + limit;
    try {
      const resp = await ABAApi.get(url);
      return {
        success: true,
        data: (resp.data.data || resp.data) as any[]
      };
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || `Server error: ${error.response.status}`
        };
      }
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }
}
